import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@zond/ui';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useGetMyIntegrations } from '../../../integrations/integrations.queries';
import { IntegrationCard } from './project-integration-card';

interface ProjectIntegrationsListProps {
  projectId: number;
  integrationMappings: Array<{ integrationType: string; externalId: string; metadata?: any }>;
  onIntegrationMappingsChange: (
    mappings: Array<{ integrationType: string; externalId: string; metadata?: any }>
  ) => void;
}

export function ProjectIntegrationsList({
  projectId,
  integrationMappings,
  onIntegrationMappingsChange,
}: ProjectIntegrationsListProps) {
  const { t } = useTranslation('projects');
  const { data: integrations } = useGetMyIntegrations();
  const { workspaceId } = useParams();

  const connectedIntegrations = integrations?.filter((int) => int.status === 'Enable') || [];

  const toggleProject = (
    integrationType: string,
    externalId: string,
    projectData?: { id: string; name: string; key?: string; description?: string }
  ) => {
    const exists = integrationMappings.some(
      (m) => m.integrationType === integrationType && m.externalId === externalId
    );

    if (exists) {
      onIntegrationMappingsChange(
        integrationMappings.filter(
          (m) => !(m.integrationType === integrationType && m.externalId === externalId)
        )
      );
    } else {
      onIntegrationMappingsChange([
        ...integrationMappings,
        {
          integrationType,
          externalId,
          metadata: projectData
            ? {
                name: projectData.name,
                key: projectData.key,
                description: projectData.description,
              }
            : undefined,
        },
      ]);
    }
  };

  if (connectedIntegrations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('integrations.title')}</CardTitle>
          <CardDescription>{t('integrations.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">{t('integrations.noneConnected')}</p>
            <Button asChild variant="outline" size="sm">
              <Link to={`/${workspaceId}/integrations`}>{t('integrations.goToIntegrations')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {connectedIntegrations.map((integration) => (
        <IntegrationCard
          key={integration.id}
          integration={integration}
          selectedMappings={integrationMappings.filter(
            (m) => m.integrationType === integration.integrationType
          )}
          onToggleProject={(externalId, projectData) =>
            toggleProject(integration.integrationType, externalId, projectData)
          }
        />
      ))}
    </div>
  );
}
